import React from 'react';
import {
    TextInput, DateInput, SelectInput, ReferenceInput, AutocompleteInput,
    required, useTranslate, useRecordContext
} from 'react-admin';
import { useController, useWatch } from 'react-hook-form';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import MuiTextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import SimpleForm from '../common/forms/SimpleForm';
import Pill from './Pill';
import { axesByBlock, targetFor } from './useReviewGrid';

const userOptionText = choice => choice?.fullname ? choice.fullname : '';

const NOT_APPLICABLE = 'na';

const KIND_FALLBACKS = { manager: 'Manager review', self: 'Self review', pair: 'Peer review' };
const STATUS_FALLBACKS = { draft: 'Draft', submitted: 'Submitted', shared: 'Shared', final: 'Final' };

export const kindLabel = (translate, kind) => translate(`review_kind.${kind}`, { _: KIND_FALLBACKS[kind] || kind });
export const statusLabel = (translate, status) => translate(`review_status.${status}`, { _: STATUS_FALLBACKS[status] || status });

const sectionSx = { width: '100%', marginBottom: '16px' };

// The Kernel theme caps full width fields at 50% of the form: the review form needs the whole width.
const formSx = {
    '& .MuiFormControl-fullWidth': { maxWidth: '100%' },
};

// Two columns on wide screens, stacked below 1100px.
const axisRowSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 11fr) minmax(0, 9fr)' },
    gap: { xs: 1, lg: 3 },
    padding: '16px 0',
    borderBottom: '1px solid',
    borderColor: 'divider',
    '&:last-of-type': { borderBottom: 'none' },
};

const toggleSx = {
    flexWrap: 'wrap',
    '& .MuiToggleButton-root': { textTransform: 'none', paddingX: 1.5, lineHeight: 1.3 },
};

export const GapChip = ({ score, target }) => {
    if (!Number.isInteger(score) || !Number.isInteger(target)) {
        return null;
    }
    const gap = score - target;
    const color = gap < 0 ? 'error' : gap > 0 ? 'success' : 'default';
    return <Pill color={color} label={gap > 0 ? `+${gap}` : `${gap}`} sx={{ minWidth: 36, justifyContent: 'center' }} />;
};

const ScoreInput = ({ grid, axis, stage }) => {
    const translate = useTranslate();
    const { field } = useController({ name: `scores.${axis.key}`, defaultValue: null });
    const entry = field.value || {};
    const selected = entry.na ? NOT_APPLICABLE : (entry.score ?? null);
    const target = targetFor(grid, stage, axis.block);

    const update = (changes) => field.onChange({ score: null, na: false, fact: null, ...entry, ...changes });

    // Clicking the selected level again clears it: the axis goes back to "not observed".
    const handleLevel = (event, value) => {
        if (value === null) {
            update({ score: null, na: false });
        } else if (value === NOT_APPLICABLE) {
            update({ score: null, na: true });
        } else {
            update({ score: value, na: false });
        }
    };

    let hint = translate('Not observed: leave empty');
    if (entry.na) {
        hint = translate('Not applicable to this person');
    } else if (Number.isInteger(entry.score)) {
        hint = axis.levels[entry.score - 1];
    }

    return (
        <Box sx={axisRowSx}>
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{axis.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{axis.question}</Typography>
                    </Box>
                    {target !== null && (
                        <Box display="flex" alignItems="center" gap={1} sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" color="text.secondary">{translate('Target')} {target}</Typography>
                            <GapChip score={entry.score} target={target} />
                        </Box>
                    )}
                </Box>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={selected}
                    onChange={handleLevel}
                    sx={{ ...toggleSx, marginTop: '10px' }}
                >
                    {grid.levels.map(level => (
                        <Tooltip key={level.value} title={axis.levels[level.value - 1]} placement="top" arrow>
                            <ToggleButton value={level.value}>{level.value} · {level.label}</ToggleButton>
                        </Tooltip>
                    ))}
                    <ToggleButton value={NOT_APPLICABLE}>{translate('N/A')}</ToggleButton>
                </ToggleButtonGroup>
                <Typography
                    variant="body2"
                    color={Number.isInteger(entry.score) ? 'text.primary' : 'text.secondary'}
                    sx={{ marginTop: '8px', fontStyle: Number.isInteger(entry.score) ? 'normal' : 'italic' }}
                >
                    {hint}
                </Typography>
            </Box>
            <MuiTextField
                multiline
                minRows={3}
                fullWidth
                label={translate('The fact behind the score')}
                placeholder={translate('What happened, when: a dated fact')}
                value={entry.fact || ''}
                onChange={(event) => update({ fact: event.target.value })}
            />
        </Box>
    );
};

const ScoresInput = ({ grid }) => {
    const stage = useWatch({ name: 'stage_current' });

    return axesByBlock(grid).map(block => (
        <Card key={block.key} variant="outlined" sx={sectionSx}>
            <CardContent>
                <Typography variant="h6">{block.label}</Typography>
                {block.axes.map(axis => <ScoreInput key={axis.key} grid={grid} axis={axis} stage={stage} />)}
            </CardContent>
        </Card>
    ));
};

const fieldsGridSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
    columnGap: 2,
    '& .MuiFormControl-root, & .MuiAutocomplete-root': { width: '100%', minWidth: '0 !important' },
};

const SubjectInputs = ({ grid, isCreate }) => {
    const translate = useTranslate();
    const record = useRecordContext();

    if (!isCreate) {
        return (
            <Typography variant="h6" sx={{ marginBottom: '12px' }}>
                {record?.user?.fullname} · {kindLabel(translate, record?.kind)}
            </Typography>
        );
    }

    if (!grid.is_manager) {
        return null;
    }

    return (
        <Box sx={fieldsGridSx}>
            <ReferenceInput source="user_id" reference="users" filter={{ active: true, is_staff: true }} sort={{ field: 'firstname', order: 'ASC' }}>
                <AutocompleteInput label={translate('Person')} optionText={userOptionText} validate={[required()]} />
            </ReferenceInput>
        </Box>
    );
};

const ReviewForm = ({ grid, isCreate = false, ...props }) => {
    const translate = useTranslate();
    const stageChoices = grid.stages.map(stage => ({ id: stage.key, name: stage.label }));

    return (
        <SimpleForm sx={formSx} {...props}>
            <Card variant="outlined" sx={sectionSx}>
                <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '16px' }}>{grid.intro}</Typography>
                    <SubjectInputs grid={grid} isCreate={isCreate} />
                    <Box sx={fieldsGridSx}>
                        <TextInput source="period_label" label={translate('Period')} helperText={translate('For example S2 2026')} />
                        <DateInput source="period_start" label={translate('From')} />
                        <DateInput source="period_end" label={translate('To')} />
                        <SelectInput source="stage_current" label={translate('Current stage')} choices={stageChoices} helperText={translate('Sets the target of each axis')} />
                        <SelectInput source="stage_target" label={translate('Target stage')} choices={stageChoices} />
                    </Box>
                </CardContent>
            </Card>

            <ScoresInput grid={grid} />

            <Card variant="outlined" sx={sectionSx}>
                <CardContent>
                    <Typography variant="h6" sx={{ marginBottom: '8px' }}>{translate('Review summary')}</Typography>
                    <TextInput
                        source="values_note"
                        label={translate('Values embodied')}
                        helperText={translate('Passion, Team, Quality and integrity, Extra mile, Innovation: which show, which is missing?')}
                        multiline
                        minRows={3}
                        fullWidth
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 2 }}>
                        <TextInput source="strengths" label={translate('Strengths')} multiline minRows={5} fullWidth />
                        <TextInput source="improvements" label={translate('Areas to improve')} multiline minRows={5} fullWidth />
                    </Box>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={sectionSx}>
                <CardContent>
                    <Typography variant="h6">{translate('Objectives (verifiable)')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '16px' }}>
                        {translate('Two objectives that can be answered yes or no at the next review.')}
                    </Typography>
                    <TextInput source="objectives" label={translate('Objectives')} multiline minRows={4} fullWidth />
                </CardContent>
            </Card>
        </SimpleForm>
    );
};

export default ReviewForm;
